import { useCallback, useEffect, useMemo } from "react";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { useDebounce, useResolvedTypedAddress } from "@sablier/v2-hooks";
import { useIsSanctioned } from "@sablier/v2-hooks";
import { useT } from "@sablier/v2-locales";
import { guards, policy } from "@sablier/v2-machines";
import { _ } from "@sablier/v2-mixins";
import { vendors } from "@sablier/v2-utils";
import { useExtensionUtilities } from "~/client/extensions";
import extensions from "~/client/extensions";
import {
  useAccount,
  useChain,
  useCreateSingleAccessor,
  useCreateSingleField,
  useCreateSingleForm,
  useExpected,
  useMachineCreateSingle,
  useModalDurationPicker,
  useModalMomentPicker,
  useModalProxy,
  useModalSimulator,
  useModalTokenPicker,
  useModalTransaction,
  useResetter,
  useTokenAllowance,
} from "~/client/hooks";
import { peripheral } from "~/client/utils";
import type { IToken } from "@sablier/v2-types";
import type { IExtensionDependencies } from "~/client/types";

export function useTimeFlip() {
  const access = useCreateSingleAccessor();
  const duration = useCreateSingleField("duration");
  const extend = useExtensionUtilities();
  const isDuration = useMemo(() => duration.field.isActive, [duration]);
  const isMoment = useMemo(() => !duration.field.isActive, [duration]);
  const { t } = useT();

  const toMoment = useMemo(
    () => ({
      to: t("structs.momentSwitch"),
      toShort: t("words.date"),
      onClick: () => {
        const state = access();
        const update = state.api.updateFields;
        const resetted = extend.reset(state.fields.extension.purpose);
        const extension = resetted ?? state.fields.extension;

        update({
          duration: { isActive: false, value: undefined, warning: undefined },
          extension,
        });
      },
    }),
    [access, extend, t],
  );

  const toDuration = useMemo(
    () => ({
      to: t("words.duration"),
      toShort: t("words.time"),
      onClick: () => {
        const state = access();
        const update = state.api.updateFields;
        const resetted = extend.reset(state.fields.extension.purpose);
        const extension = resetted ?? state.fields.extension;

        update({
          end: { value: undefined, warning: undefined },
          start: { value: undefined, warning: undefined },
          duration: { isActive: true },
          extension,
        });
      },
    }),
    [access, extend, t],
  );

  return {
    isDuration,
    isMoment,
    toDuration,
    toMoment,
  };
}

export function useExtension() {
  const extension = useCreateSingleField("extension");
  return {
    extension,
  };
}

export function useFieldAddress() {
  const { chain } = useChain();
  const access = useCreateSingleAccessor();
  const address = useCreateSingleField("address");
  const { t } = useT();

  const label = useMemo(
    () => ({
      icon: QuestionMarkCircleIcon,
      isIconLast: true,
      tooltip: {
        value: t("errors.addressAbility"),
        arrow: true,
      },
      value: _.capitalize(t("words.recipient")),
    }),
    [t],
  );

  /** --- When the input changes values, update the resolution --- */
  const resolution = useResolvedTypedAddress(
    address.field.value,
    chain?.chainId,
  );

  /** --- When the field loses focus, test vales without resolution (we're working async) --- */
  const onBlur = useCallback(() => {
    const state = access();
    const field = state.fields.address;
    const chainId = chain?.chainId;
    const update = state.api.updateFields;

    if (_.isNilOrEmptyString(field.value)) {
      update({
        address: {
          isFocused: false,
          warning: undefined,
        },
      });
    } else {
      update({
        address: {
          isFocused: false,
          warning: guards.validateAccount({
            t,
            chainId,
            value: field.value,
          }),
        },
      });
    }
  }, [access, chain, t]);

  const onFocus = useCallback(() => {
    access().api.updateFields({ address: { isFocused: true } });
  }, [access]);

  /** --- When the input changes values, reset dependencies --- */
  const onChange = useCallback(
    (value: string | undefined) => {
      const state = access();
      const update = state.api.updateFields;
      update({ address: { value, resolution: undefined, warning: undefined } });
    },
    [access],
  );

  /** --- When the chain is changed, re-test the values --- */
  useEffect(() => {
    const state = access();
    const field = state.fields.address;
    if (!_.isNilOrEmptyString(field.value)) {
      onBlur();
    }
  }, [access, chain, onBlur]);

  /** --- When resolution happens, move its values in the store --- */
  useEffect(() => {
    const state = access();
    const update = state.api.updateFields;
    update({
      address: {
        isLoading: resolution.isLoading,
        resolution: _.pick(resolution, ["address", "ens", "preview", "short"]),
      },
    });
  }, [access, resolution]);

  /** --- When the field moves out-of-focus and the resolution is ready, validate it --- */
  useEffect(() => {
    if (
      !address.field.isFocused &&
      !address.field.isLoading &&
      !_.isNil(address.field.resolution)
    ) {
      const state = access();
      const chainId = chain?.chainId;
      const update = state.api.updateFields;

      if (!_.isNilOrEmptyString(address.field.value)) {
        update({
          address: {
            warning: guards.validateAccount({
              t,
              address: address.field.resolution.address,
              chainId,
              isResolved: true,
              value: address.field.value,
            }),
          },
        });
      }
    }
  }, [access, address, chain, t]);

  return {
    address,
    isLoading: resolution.isLoading,
    label,
    onBlur,
    onChange,
    onFocus,
  };
}

export function useFieldAmount() {
  const access = useCreateSingleAccessor();
  const amount = useCreateSingleField("amount");
  const max = useCreateSingleField("max");
  const token = useCreateSingleField("token");
  const { t } = useT();

  const maximum = useMemo(
    () => ({
      label: `${t("words.max")}:`,
      value:
        max.field.value && max.field.value !== "0"
          ? _.toNumeralPrice(max.field.value, false)
          : undefined,
    }),
    [max, t],
  );

  const onBlur = useCallback(() => {
    const state = access();
    const max = state.fields.max.value;
    const value = state.fields.amount.value;
    const update = state.api.updateFields;

    if (_.isNilOrEmptyString(value)) {
      update({ amount: { warning: undefined } });
    } else {
      update({
        amount: {
          warning: guards.validateAmount({
            t,
            context: "wallet",
            max,
            min: "0",
            value,
          }),
        },
      });
    }
  }, [access, t]);

  const onChange = useCallback(
    (value: string | undefined) => {
      const state = access();
      const update = state.api.updateFields;
      update({ amount: { value, warning: undefined } });
    },
    [access],
  );

  const onMax = useCallback(() => {
    vendors.track.log((events) => events.clickMax("createSingle"));
    const state = access();
    const update = state.api.updateFields;
    update({
      amount: {
        value: state.fields.max.value,
      },
    });

    onBlur();
  }, [access, onBlur]);

  useEffect(() => {
    onBlur();
  }, [onBlur, max]);

  return {
    amount,
    maximum,
    onBlur,
    onChange,
    onMax,
    token,
  };
}

export function useFieldCancelability() {
  const cancelability = useCreateSingleField("cancelability");
  const { t } = useT();

  const value = useMemo(() => {
    return {
      isChecked: cancelability.field.value,
      title: t("words.cancelability"),
    };
  }, [cancelability.field, t]);

  return {
    cancelability,
    value,
  };
}

export function useFieldDuration() {
  const access = useCreateSingleAccessor();
  const duration = useCreateSingleField("duration");
  const { setOpen } = useModalDurationPicker();

  const onConfirm = useCallback(
    (value?: string) => {
      const state = access();
      const update = state.api.updateFields;
      update({
        duration: {
          value,
        },
      });
    },
    [access],
  );

  const onClick = useCallback(() => {
    vendors.track.log((events) =>
      events.openModalFrom({
        nameKey: "duration",
        placeKey: "createSingle",
      }),
    );
    setOpen(true, {
      milliseconds: duration.field.value,
      onConfirm,
    });
  }, [duration, onConfirm, setOpen]);

  return { duration, onClick, onConfirm };
}

export function useFieldEnd() {
  const access = useCreateSingleAccessor();
  const end = useCreateSingleField("end");
  const { setOpen } = useModalMomentPicker();
  const { t } = useT();

  const onConfirm = useCallback(
    (value?: string) => {
      const state = access();
      const update = state.api.updateFields;
      const start = state.fields.start.value;
      let warning = undefined;

      if (!_.isNilOrEmptyString(value)) {
        warning = guards.validateMoment({
          t,
          value,
          min: start,
        });
        if (_.isNilOrEmptyString(warning)) {
          warning = guards.validateMoment({
            isExcess: true,
            purpose: "datetoday",
            t,
            value,
            min: new Date().valueOf().toString(),
          });
        }
      }

      update({
        end: {
          value,
          warning,
        },
        start: {
          warning: undefined,
        },
      });
    },
    [access, t],
  );

  const onClick = useCallback(() => {
    vendors.track.log((events) => {
      return events.openModalFrom({
        nameKey: "moment",
        placeKey: "createSingle",
      });
    });
    setOpen(true, {
      milliseconds: end.field.value,
      onConfirm,
      purpose: t("structs.endDate"),
    });
  }, [onConfirm, setOpen, end, t]);

  const placeholder = useMemo(
    () => _.toDuration(new Date().valueOf(), "date")[0],
    [],
  );

  return { end, placeholder, onClick };
}

export function useFieldShape() {
  const shape = useCreateSingleField("shape");
  return { shape };
}

export function useFieldStart() {
  const { t } = useT();
  const access = useCreateSingleAccessor();
  const start = useCreateSingleField("start");
  const { setOpen } = useModalMomentPicker();

  const onConfirm = useCallback(
    (value?: string) => {
      const state = access();
      const update = state.api.updateFields;
      const end = state.fields.end.value;
      let warning = undefined;

      if (!_.isNilOrEmptyString(value)) {
        warning = guards.validateMoment({
          t,
          max: end,
          value,
        });
      }

      update({
        end: {
          warning: undefined,
        },
        start: {
          value,
          warning,
        },
      });
    },
    [access, t],
  );

  const onClick = useCallback(() => {
    vendors.track.log((events) => {
      return events.openModalFrom({
        nameKey: "moment",
        placeKey: "createSingle",
      });
    });
    setOpen(true, {
      milliseconds: start.field.value,
      onConfirm,
      purpose: t("structs.startDate"),
    });
  }, [onConfirm, setOpen, start, t]);

  const placeholder = useMemo(
    () => _.toDuration(new Date().valueOf(), "date")[0],
    [],
  );

  return { onClick, placeholder, start };
}

export function useFieldToken() {
  const { t } = useT();
  const token = useCreateSingleField("token");
  const { setOpen } = useModalTokenPicker();
  const { isSanctioned } = useIsSanctioned(token.field.value?.address);

  const onChoose = useCallback(
    (value?: IToken) => {
      vendors.track.log((events) => {
        return events.selectToken(value?.name ?? "");
      });
      token.update({
        value: {
          ...value!,
          image: value?.image ?? undefined,
        },
        warning: undefined,
      });
    },
    [token],
  );

  const onChange = useCallback(() => {
    vendors.track.log((events) => {
      return events.openModalFrom({
        nameKey: "tokenPicker",
        placeKey: "createSingle",
      });
    });
    setOpen(true, {
      chosen: token.field.value,
      onChoose,
    });
  }, [onChoose, setOpen, token]);

  /** Update the token warning if the asset is resolved as sanctioned by TRM */
  useEffect(() => {
    if (isSanctioned) {
      token.update({ warning: policy.token.sanctioned(t) });
    }
  }, [isSanctioned, t, token]);

  return {
    token,
    onChange,
    onChoose,
  };
}

export function useFormPrepare() {
  const { t } = useT();
  const [amount, token] = useCreateSingleForm((state) => [
    state.fields.amount,
    state.fields.token,
    state.fields.extension,
  ]);

  const fields = useCreateSingleForm((state) => state.fields);
  const { machine, send } = useMachineCreateSingle();
  const { isConnected, proxy, isConnecting } = useAccount();

  const permit2Address = useMemo(
    () => peripheral(fields.token.value?.chainId, "permit2").address,
    [fields],
  );

  const error = useDebounce(machine.context.error, 300);

  const allowance = useTokenAllowance({
    allowance: amount.value,
    spender: permit2Address,
    token: token.value,
  });

  const isPrepared = useMemo(
    () =>
      isConnected &&
      !_.isNilOrEmptyString(proxy) &&
      allowance.debounced === "allowed" &&
      _.isNilOrEmptyString(error),
    [allowance.debounced, error, isConnected, proxy],
  );

  const hasProxy = useMemo(
    () => !isConnecting && !_.isNilOrEmptyString(proxy),
    [isConnecting, proxy],
  );

  useEffect(() => {
    send("CHECK", { payload: { api: { t }, fields } });
  }, [fields, t, send]);

  return {
    allowance,
    isPrepared,
    hasProxy,
  };
}

export function useFormSubmit() {
  const access = useCreateSingleAccessor();
  const { machine, send } = useMachineCreateSingle();
  const { setOpen, updateData, doClose } = useModalTransaction();
  const { doOpen } = useModalProxy();
  const { signer, isConfigured, proxy } = useAccount();
  const { track } = useExpected();
  const { all: reset } = useResetter();
  const error = useMemo(() => machine.context.error, [machine.context.error]);
  const { t } = useT();

  const doSubmit = useCallback(() => {
    const state = access();
    if (isConfigured) {
      send("SAVE", {
        payload: {
          api: {
            reset,
            setOpen,
            t,
            track,
            updateData,
            doClose,
            openProxyModal: doOpen,
          },
          fields: state.fields,
          signer,
          proxy,
        },
      });
    }
  }, [
    access,
    isConfigured,
    reset,
    send,
    setOpen,
    doClose,
    doOpen,
    signer,
    t,
    track,
    updateData,
    proxy,
  ]);

  return {
    doSubmit,
    error,
  };
}

export function useFormSimulation() {
  const access = useCreateSingleAccessor();
  const { setOpen: setOpenSimulator } = useModalSimulator();
  const { signer } = useAccount();
  const simulate = useCallback(() => {
    const state = access();
    const timing = state.fields.duration.isActive ? "duration" : "range";

    const dependencies = {
      amount: state.fields.amount.value!,
      address: state.fields.address.value,
      cancelability: state.fields.cancelability.value,
      chainId: 5,
      duration: state.fields.duration.value,
      end: state.fields.end.value,
      start: state.fields.start.value,
      token: state.fields.token.value,
      sender: "",
      proxy: "",
      signer: signer!,
    } satisfies IExtensionDependencies<"single">;

    const stream = extensions.simulate({
      dependencies,
      extras: state.fields.extension,
      timing,
    });

    setOpenSimulator(true, { stream });
  }, [access, setOpenSimulator, signer]);

  return {
    simulate,
  };
}
